import RegexEscape from 'regex-escape';
import { IGNORED_NODE_NAME, NODE_TOKENIZER_CUSTOM_ATTRS } from './utils';

class RulesHandling {
    /**
     * Initialize the rules handling class
     * @param {any[]} rules Initial rules to be applied
     */
    constructor(rules = []) {
        /**
         * The list of rules exclusions to be applied
         * 
         * @type {any[]}
         */
        this.exclusion_rules = [];
        /**
         * The list of text replacement to be applied
         * 
         * @type {any[]}
         */
        this.text_replacement_rules = [];
        /**
         * The list of text ignore to be applied
         * 
         * @type {any[]}
         */
        this.text_ignore_rules = [];
        /**
         * The list of dynamic content rules to be applied
         * 
         * @type {any[]}
         */
        this.dynamic_content_rules = [];
        /**
         * The list of exclusions by URL to be applied.
         * 
         * @type {any[]}
         */
        this.pause_dynamic_content_rules = [];

        rules.forEach((rule) => {
            if (!rule.apply_to_url) {
                return;
            }
            if (!rule.apply_to_language) {
                return;
            }

            switch (rule.type) {
                case 1:
                    this.text_ignore_rules.push(rule.params);
                    break;
                case 2:
                    this.text_replacement_rules.push(rule.params);
                    break;
                case 3:
                    this.exclusion_rules.push(rule.params);
                    break;
                case 4:
                    this.pause_dynamic_content_rules.push(rule.params);
                    break;
                case 6:
                    this.dynamic_content_rules.push(rule.params);
                    break;
            }
        });
    }

    /**
     * Check if a list of selectors contains the element in child
     * @param {Node} element the element to be checked
     * @param {string[]} selectors The selectors to be checked
     * 
     * @returns {Element | null} The matching element
     */
    findMatchingSelector(element, selectors) {
        if (!element) {
            return null;
        }

        if (!selectors || selectors.length === 0) {
            return null;
        }

        for (const selector of selectors) {
            const queryAlls = document.querySelectorAll(selector);
            if (queryAlls.length === 0) {
                continue;
            }

            for (const queryAll of Array.from(queryAlls)) {
                if (queryAll.contains(element)) {
                    return queryAll;
                }
            }
        }

        return null;
    }

    /**
     * Check if there are rules that pause dynamic content
     *
     * !! NOTE !!
     * This will break website that use history-router system to navigate through URL.
     * Example are vue-router, react-router-dom, etc.
     *
     * @returns {boolean} If there are rules that pause dynamic content
     */
    shouldPauseDynamicContent() {
        // Check for this rules that doesn't have multilingual enabled
        // If any of them is found, then we pause the dynamic content
        return this.pause_dynamic_content_rules.findIndex((rule) => rule.multilingual_url === false) !== -1;
    }

    /**
     * Check if an element is exlucded from the rules
     * @param {Node} element node element
     */
    isElementRulesExcluded(element) {
        return this.exclusion_rules
            .map((rule) => {
                const selectors = rule.element_selectors || [];
                return this.findMatchingSelector(element, selectors) !== null;
            })
            .filter((el) => el)
            .length > 0;
    }

    /**
     * Is the text match the target
     * @param {string} text The text to match
     * @param {string} target The target match
     * @param {"wildcard" | "regex"} mode The match mode
     * @param {boolean} ignore_case Ignore case
     */
    isTextMatch(text, target, mode, ignore_case = false) {
        if (!text) {
            return false;
        }

        let indicators = 'gs';
        if (ignore_case) {
            indicators += 'i';
        }

        const regexQuery = mode === 'regex' ? `(${target})` : `(${RegexEscape(target)})`;
        const regex = new RegExp(regexQuery, indicators);

        return regex.test(text);
    }

    /**
     * Check if an element has any of the attributes
     * 
     * @param {Element} element The element to be checked
     * @param {string[]} attributes The attributes to be checked
     */
    isElementHasAttributes(element, attributes) {
        if (!element) {
            return false;
        }

        if (!attributes || attributes.length === 0) {
            return false;
        }

        return attributes
            .map((attr) => element.hasAttribute(attr))
            .filter((el) => el)
            .length > 0;
    }

    /**
     * Apply class for text replacement and other
     * @param {import('./TranslationNode')} nodes List of nodes to apply the rules
     */
    attachTextRules(nodes) {
        /** @type {string[]} */
        const tokenizedTextOnly = nodes.tokenize().map((token) => token.text)
        this.text_ignore_rules.forEach((rule) => {
            if (rule.languages && !rule.languages.includes(this.current_language)) {
                // skip if not in the language
                return;
            }

            const textIgnored = rule.text_ignore
                .map((caseRule) => {
                    return tokenizedTextOnly
                        .map((text) => {
                            return this.isTextMatch(text, caseRule.text_ignore, caseRule.text_ignore_type, caseRule.ignore_case);
                        })
                        .filter((el) => el)
                        .length > 0;
                })
                .filter((el) => el)
                .length > 0;

            if (textIgnored) {
                const matchSelector = this.findMatchingSelector(nodes.element, rule.element_selectors || []);
                if (matchSelector) {
                    nodes.extraMetadata['id'] = nodes.extraMetadata['id'] || matchSelector.id;
                    nodes.extraMetadata['class'] = [...(nodes.extraMetadata['class'] || []), ...matchSelector.className.split(' ')];
                    nodes.extraMetadata['dataset'] = {...(nodes.extraMetadata['dataset'] || {}), ...matchSelector.dataset};
                }
            }
        });

        this.text_replacement_rules.forEach((rule) => {
            if (rule.languages && !rule.languages.includes(this.current_language)) {
                // skip if not in the language
                return;
            }

            const sourceTextReplaced = rule.text_replace
                .filter((caseRule) => caseRule.source_type === 'original')
                .map((caseRule) => {
                    return tokenizedTextOnly
                        .map((text) => {
                            return this.isTextMatch(text, caseRule.text_replace, caseRule.text_replace_type, caseRule.ignore_case);
                        })
                        .filter((el) => el)
                        .length > 0;
                })
                .filter((el) => el)
                .length > 0;

            if (sourceTextReplaced) {
                const matchSelector = this.findMatchingSelector(nodes.element, rule.element_selectors || []);
                if (matchSelector) {
                    nodes.extraMetadata['id'] = nodes.extraMetadata['id'] || matchSelector.id;
                    nodes.extraMetadata['class'] = [...(nodes.extraMetadata['class'] || []), ...matchSelector.className.split(' ')];
                    nodes.extraMetadata['dataset'] = {...(nodes.extraMetadata['dataset'] || {}), ...matchSelector.dataset};
                }
            }
        });
    }

    /**
     * Collect the dynamic nodes that explicitly defined in the rules
     *
     * @returns {[Node, Element][]} List of text nodes
     */
    collectDynamicNodes() {
        /** @type {[Node, HTMLElement][]} */
        const dynamicNodes = [];
        for (const rule of this.dynamic_content_rules) {
            const elementSelectors = rule.element_selectors || [];

            if (!elementSelectors || elementSelectors.length === 0) {
                continue;
            }

            /** @type {Element[]} */
            const queryAlls = elementSelectors
                .map((selector) => Array.from(document.querySelectorAll(selector)))
                // flatten
                .reduce((acc, el) => [...acc, ...el], []);

            if (queryAlls.length === 0) {
                continue;
            }

            const seenNodes = new Set();
            for (const queryAll of queryAlls) {
                if (seenNodes.has(queryAll)) {
                    continue;
                }

                seenNodes.add(queryAll);

                // tree walker for Text and Element nodes
                const walker = document.createTreeWalker(queryAll, NodeFilter.SHOW_TEXT | NodeFilter.SHOW_ELEMENT, (node) => {
                    // Is element exist in DOM
                    if (!node.isConnected) {
                        return NodeFilter.FILTER_REJECT;
                    }

                    // Ignore comment node
                    if (node.nodeType === Node.COMMENT_NODE) {
                        return NodeFilter.FILTER_REJECT;
                    }

                    // Do not process element node that does not have custom tokenizer
                    // and does not have any custom tokenizer attributes
                    if (node.nodeType === Node.ELEMENT_NODE && this.isElementHasAttributes(node, NODE_TOKENIZER_CUSTOM_ATTRS[node.nodeName.toUpperCase()] || [])) {
                        return NodeFilter.FILTER_SKIP;
                    }

                    // Get parent
                    let parentEl = node;
                    if (node.nodeType === Node.TEXT_NODE) {
                        parentEl = node.parentElement;
                    }

                    if (IGNORED_NODE_NAME.includes(node.nodeName.toUpperCase())) {
                        return NodeFilter.FILTER_REJECT;
                    }

                    if (IGNORED_NODE_NAME.includes(parentEl.nodeName.toUpperCase())) {
                        return NodeFilter.FILTER_REJECT;
                    }

                    return NodeFilter.FILTER_ACCEPT;
                });

                while (walker.nextNode()) {
                    if (walker.currentNode.nodeType === Node.TEXT_NODE) {
                        if (!walker.currentNode.parentElement) {
                            continue;
                        }
                        dynamicNodes.push([walker.currentNode, walker.currentNode.parentElement]);
                    } else {
                        dynamicNodes.push([walker.currentNode, walker.currentNode]);
                    }
                }
            }
        }

        return dynamicNodes;
    }
}

export default RulesHandling;
